/**
 * Advanced connectivity API client (S2) — coherence, PLI, connectivity map.
 */

import apiClient from "@shared/services/apiClient";

export type CoherenceResult = {
  recordingId: string;
  channelPair: [string, string];
  frequencies: number[];
  coherence: number[];
  meanCoherence: number;
  significantPeaks?: Array<{ frequency: number; coherence: number }>;
  timeResolution?: number;
  timestamp?: string;
};

export type PliResult = {
  recordingId: string;
  channelPair: [string, string];
  frequencies: number[];
  pli: number[];
  meanPLI: number;
  frequencyBandPLI?: Record<string, number>;
  timestamp?: string;
};

export type ConnectivityMapResult = {
  recordingId: string;
  method: "pli" | "coherence";
  channels: string[];
  connectivityMatrix: number[][];
  timestamp?: string;
};

export async function postCoherence(input: {
  recordingId: string;
  channel1: string;
  channel2: string;
  frequencyRange?: [number, number];
}): Promise<CoherenceResult> {
  const response = await apiClient.post("/advanced-analysis/coherence", input);
  return (response.data?.data ?? response.data) as CoherenceResult;
}

export async function postPli(input: {
  recordingId: string;
  channel1: string;
  channel2: string;
  frequencyRange?: [number, number];
}): Promise<PliResult> {
  const response = await apiClient.post("/advanced-analysis/pli", input);
  return (response.data?.data ?? response.data) as PliResult;
}

export async function getConnectivityMap(
  recordingId: string,
  method: "pli" | "coherence" = "pli",
): Promise<ConnectivityMapResult> {
  try {
    const response = await apiClient.get(
      `/advanced-analysis/connectivity-map/${encodeURIComponent(recordingId)}`,
      { params: { method } },
    );
    return (response.data?.data ?? response.data) as ConnectivityMapResult;
  } catch (error: unknown) {
    const axiosError = error as {
      response?: { status?: number; data?: { error?: string; message?: string } };
      message?: string;
    };
    const status = axiosError.response?.status;
    const serverMessage =
      axiosError.response?.data?.error ||
      axiosError.response?.data?.message ||
      axiosError.message ||
      "Connectivity map request failed";
    if (status === 404) {
      throw new Error(
        `${serverMessage} (HTTP 404). Confirm the recording exists and connectivity-map is deployed for this environment.`,
      );
    }
    throw new Error(
      status ? `${serverMessage} (HTTP ${status})` : serverMessage,
    );
  }
}

export async function fetchStoredSpectral(
  recordingId: string,
  channel?: number,
): Promise<{ rows: any[]; source: "server" | "empty" }> {
  try {
    const response = await apiClient.get(
      `/analysis/spectral/${encodeURIComponent(recordingId)}`,
      {
        params: {
          ...(channel != null ? { channel } : {}),
          limit: 50,
        },
      },
    );
    const rows = response.data?.data ?? response.data?.analyses ?? response.data;
    const list = Array.isArray(rows) ? rows : [];
    return { rows: list, source: list.length ? "server" : "empty" };
  } catch {
    return { rows: [], source: "empty" };
  }
}

export async function fetchStoredSpectrogram(
  recordingId: string,
  channelIndex: number,
): Promise<{ matrix: number[][] | null; source: "server" | "empty" }> {
  try {
    const response = await apiClient.get(
      `/analysis/spectrogram/${encodeURIComponent(recordingId)}`,
      { params: { channel: channelIndex } },
    );
    const payload = response.data?.data ?? response.data;
    const matrix =
      payload?.magnitudes ||
      payload?.matrix ||
      payload?.spectrogram_data ||
      null;
    if (Array.isArray(matrix) && matrix.length) {
      return { matrix, source: "server" };
    }
    return { matrix: null, source: "empty" };
  } catch {
    return { matrix: null, source: "empty" };
  }
}

export async function fetchQeegAnalysisSummary(recordingId: string): Promise<{
  recording_id: string;
  bands: Record<string, number | null>;
  available: boolean;
  message?: string;
  trendsRef?: string;
}> {
  const response = await apiClient.get(
    `/analysis/qeeg/${encodeURIComponent(recordingId)}`,
  );
  return (response.data?.data ?? response.data) as any;
}
